import { IconChevronDown, NotificationIcon } from '../icons/index.js'
import { getStoredUser } from '../../lib/auth.js'

/**
 * The sidebar's 56px profile row — Figma `131:8830` (profile-thumb), in situ on `133:15971`.
 * DESIGN-HANDOVER.md §8.1 item 1: avatar 32×32 circle + name + chevron-down at left, notification
 * bell with a red unread dot at right.
 *
 * MEASURED, re-pulled 2026-08-27: the name is `Body/Default/Strong` — 12px, weight 600,
 * line-height 15 (text node `131:8834`). The handover records `Body/Medium/Medium` 14px at :531;
 * that is wrong at source and is being corrected there separately.
 *
 * TWO HONEST GAPS, both rendered as absence rather than invention:
 *
 * 1. **The name may be unknown.** The JWT carries only `userId`/`role`/`permissions`, and there
 *    is no `GET /auth/me`. `login()` now stores the user it already receives, but any session
 *    that signed in earlier has a token and no stored name. When unknown this renders the
 *    avatar and NO name — it does not fall back to the Figma's "John Bainner", which would put a
 *    fabricated identity on the user's own screen, nor to "User", which asserts a name we do not
 *    have. Sign out and back in and the real name appears.
 *
 * 2. **Neither control opens anything.** §8.1: "Menu/panel contents are not evidenced for either
 *    control." The profile menu and the notification feed are never opened in any frame, so
 *    there is nothing to build and nothing is invented. Both render as structure. The chevron is
 *    `aria-hidden` and the row is NOT a button, because a control that looks actionable and does
 *    nothing is a worse lie than one that plainly does not act yet.
 *
 * The bell's unread dot is likewise driven by no real feed. `hasNotification` is FALSE here —
 * there is no notifications backend, and a red dot that is always on would assert unread items
 * that do not exist. `NotificationIcon` supports both states for when a feed lands.
 */
export function ProfileRow() {
  const user = getStoredUser()

  // Node `131:8868`: `p-12` with `justify-between` — the 56px height is what 12 + 32 + 12 comes
  // to, not a fixed value. Shipped `px-6`, which sat the avatar 6px from the edge instead of 12.
  // Re-measured 2026-08-29 after the owner reported the sidebar did not match the design.
  return (
    <div className="flex items-center justify-between gap-small p-large">
      {/* No avatar image exists anywhere: the Figma shows a photograph, `users` has no avatar
          column, and no asset contract covers avatars. The initial is derived from the real
          name when known, and is a neutral circle when it is not. */}
      <span
        aria-hidden
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border-primary bg-surface-tertiary text-body-default-strong text-text-secondary"
      >
        {user ? user.name.trim().charAt(0).toUpperCase() : ''}
      </span>

      {user ? (
        <span className="truncate text-body-default-strong text-text-default">{user.name}</span>
      ) : (
        // Deliberately empty, not a placeholder string. See gap 1 above.
        <span className="sr-only">Oturum açık kullanıcı adı bilinmiyor</span>
      )}

      <IconChevronDown aria-hidden className="shrink-0 text-icon-secondary" />

      <span className="ml-auto flex shrink-0 items-center">
        {/*
         * `title` — NOT `aria-hidden`. This was `aria-hidden` until round 3 of
         * /design-change-review (2026-08-28) knocked down the reasoning behind it.
         *
         * The argument had been: the bell is inert (no menu, no feed — §8.1 records that "menu/
         * panel contents are not evidenced for either control"), so naming it would announce a
         * control that does not exist, and silence is more honest. That sounded right and was
         * wrong, because it ignored what this codebase already has: `NotificationIcon` takes a
         * `title` that sets `role="img"` and NOT an interactive role
         * (`NotificationIcon.tsx:36-38`) — built by Epic 52 Phase 07 for exactly this case — and
         * `AiMarker.tsx` already uses that same pattern for a purely visual, non-interactive mark.
         *
         * So the choice was never "name it and imply a button" versus "stay silent". It was
         * "name it as an image" versus "hide it entirely", and hiding it left a screen-reader user
         * with no idea a notification affordance exists in the profile row while a sighted user
         * can see it. That is an information-parity gap the project's own tooling was built to
         * close. The honest reading: there is no unread STATE to announce yet, which is why
         * `hasNotification` is false — not that the icon should be nameless.
         */}
        <NotificationIcon
          hasNotification={false}
          title="Bildirimler"
          className="text-icon-default"
        />
      </span>
    </div>
  )
}
